import {useTranslation} from 'react-i18next';
import {useRouteMatch} from 'react-router-dom';
import {translations} from '../../../../../../i18n/translations';
import {CategorizedApplication} from '../../../../../../backend/responses/categorized-application';
import {ApplicationsRepository} from '../../../../../../repositories/applications';
import {PaginatedTableData} from '../../../../../../interfaces/paginated-table-data';
import {Box, Stack} from '@mui/material';
import GenericHeader from '../../../../../../components/generic-header/generic-header';
import SearchInput from '../../../../../../components/search-input/search-input';
import {usePaginatedTable} from '../../../../../../hooks';
import {PaginatedTable} from '../../../../../../components/paginated-table/paginated-table';

// Columns IDs
enum ColumnIdentifier {
  Name = 'name',
  Category = 'category',
  State = 'state',
}

export default function ApplicationsSystemAdministratorOfficeScreen() {
  const {t} = useTranslation();
  let {path} = useRouteMatch();

  // Fetch applications
  const handleGetApplications = async (limit: number, skip: number, search?: string) => {
    // Load applications
    const result: {totalCount: number; data: Array<CategorizedApplication>} =
      await ApplicationsRepository.instance.getAsSystemAdministrator(skip, limit, search);

    // Process data
    const processedData: PaginatedTableData<CategorizedApplication> = result.data.map((obj) => {
      // Return row values
      return {
        id: obj.application.id || '',
        item: obj,
        enabled: obj.application.isActive,
        avatarUrl: obj.application.picture,
        toLink: `${path}/${obj.application.id}`,
        columns: [
          {
            columnId: ColumnIdentifier.Name,
            value: obj.application.name || '',
          },
          {
            columnId: ColumnIdentifier.Category,
            value: obj.category.name || '',
          },
          {
            columnId: ColumnIdentifier.State,
            value: obj.application.isActive
              ? t(translations.applications.system_administrator.root.state_value_true)
              : t(translations.applications.system_administrator.root.state_value_false),
          },
        ],
      };
    });

    // Return result
    return {
      data: processedData,
      totalCount: result.totalCount,
    };
  };

  const {
    data,
    isLoading,
    error,
    pagesCount,
    currentPage,
    currentSearch,
    handlePageChange,
    handleApplyFilters,
    handleUpdateCurrentSearch,
  } = usePaginatedTable<CategorizedApplication>({
    fetchData: handleGetApplications,
  });

  return (
    <Box display="flex" flexDirection="column" flexGrow={1}>
      <GenericHeader
        pageTitle={t(translations.screen_paths.senior_applications)}
        isLoading={false}
        leftColumn={
          <Stack
            gap={2}
            sx={{
              display: 'flex',
              flexDirection: 'row',
              width: '100%',
              paddingTop: '10px',
            }}
          >
            <SearchInput
              placeholder={t(translations.user_applications.search_input_placeholder)}
              value={currentSearch}
              onChange={(event) => handleUpdateCurrentSearch(event.target.value)}
              onSearchAction={(value: string) => handleApplyFilters(1, value)}
            />
          </Stack>
        }
        rightColumn={null}
      />
      <Box className="listing-container">
        <Box className="listing-container-centered">
          <PaginatedTable
            avatar={{
              type: 'other',
            }}
            emptyTableMessage={t(
              translations.applications.system_administrator.root.no_applications
            )}
            columns={[
              {
                columnId: ColumnIdentifier.Name,
                title: t(translations.applications.system_administrator.root.name_header),
                flexGrow: 1,
              },
              {
                columnId: ColumnIdentifier.Category,
                title: t(translations.applications.system_administrator.root.category_header),
                flexGrow: 1,
              },
              {
                columnId: ColumnIdentifier.State,
                title: t(translations.applications.system_administrator.root.state_header),
                flexGrow: 1,
              },
            ]}
            rows={data}
            isLoading={isLoading}
            error={error}
            pagesCount={pagesCount}
            currentPage={currentPage}
            handlePageChange={handlePageChange}
            linkableRows={false}
          />
        </Box>
      </Box>
    </Box>
  );
}
